import React from "react";
import { motion } from "framer-motion";
import { Dumbbell, CalendarDays, Utensils, BookOpen, Activity, Bell } from "lucide-react";
import { FEATURES, IMAGES } from "@/data/content";
import { optimizeImg } from "@/lib/img";

const ICONS = { Dumbbell, CalendarDays, Utensils, BookOpen, Activity, Bell };

export const FeatureGrid = () => {
  return (
    <section id="features" data-testid="features-section" className="relative py-24 md:py-32 bg-charcoal/40">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-14">
          <span className="text-xs font-bold tracking-[0.25em] uppercase text-vitality">Core Features</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight mt-4 leading-tight">
            Everything That Transforms Your Journey
          </h2>
        </div>

        {/* Bento / tetris grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 auto-rows-[minmax(180px,auto)]">
          {/* Big image tile */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="md:col-span-7 md:row-span-2 relative rounded-2xl overflow-hidden border border-white/10 group"
          >
            <img src={optimizeImg(IMAGES.workout, 1000)} alt="Workout database" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-8">
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-vitality">Program 01</span>
              <h3 className="font-heading text-3xl font-bold uppercase tracking-tight mt-2">Workout Database & HD Guides</h3>
              <p className="text-white/70 mt-2 max-w-md">A full library of expert-led exercises with HD videos, smart filtering and modifications for any level.</p>
            </div>
          </motion.div>

          {/* Two stacked feature cards */}
          {FEATURES.slice(0, 2).map((f, i) => {
            const Icon = ICONS[f.icon];
            return (
              <motion.div
                key={f.title}
                data-testid={`feature-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="md:col-span-5 bg-charcoal border border-white/10 rounded-2xl p-7 hover:border-vitality/40 transition-colors"
              >
                <Icon className="w-7 h-7 text-vitality mb-4" />
                <h3 className="font-heading text-xl font-bold uppercase tracking-tight">{f.title}</h3>
                <p className="text-white/60 mt-2">{f.desc}</p>
              </motion.div>
            );
          })}

          {/* Remaining feature cards */}
          {FEATURES.slice(2).map((f, i) => {
            const Icon = ICONS[f.icon];
            return (
              <motion.div
                key={f.title}
                data-testid={`feature-${i + 2}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="md:col-span-3 bg-charcoal border border-white/10 rounded-2xl p-6 hover:border-vitality/40 hover:-translate-y-1 transition-all duration-300"
              >
                <Icon className="w-6 h-6 text-vitality mb-3" />
                <h3 className="font-heading text-lg font-bold uppercase tracking-tight leading-tight">{f.title}</h3>
                <p className="text-white/55 text-sm mt-2">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
